/**
 * Backend Task Firestore Converter (Client SDK)
 *
 * Converts between BackendTask TypeScript type and Firestore data.
 * Handles Date <-> Timestamp conversion for date fields.
 *
 * Also converts phase-level timestamps (startedAt, completedAt)
 * within the phases dictionary.
 *
 * Collection path: projects/{projectId}/backend_tasks
 */

import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
  DocumentData,
  WithFieldValue,
  PartialWithFieldValue,
  SetOptions,
} from 'firebase/firestore';
import type { BackendTask, TaskPhaseDefinition } from '../types';

/**
 * Convert Date fields to Firestore Timestamps for writing
 */
function convertToFirestore(task: Record<string, unknown>): DocumentData {
  const result: Record<string, unknown> = { ...task };

  // Remove id field (stored as document ID, not in data)
  delete result.id;

  // Convert top-level Date → Timestamp
  for (const field of ['createdAt', 'updatedAt', 'cancelledAt'] as const) {
    if (result[field] instanceof Date) {
      result[field] = Timestamp.fromDate(result[field] as Date);
    }
  }

  // Convert phase-level Date → Timestamp
  if (result.phases && typeof result.phases === 'object') {
    const phases = { ...(result.phases as Record<string, Record<string, unknown>>) };
    for (const key of Object.keys(phases)) {
      const phase = { ...phases[key] };
      for (const field of ['startedAt', 'completedAt'] as const) {
        if (phase[field] instanceof Date) {
          phase[field] = Timestamp.fromDate(phase[field] as Date);
        }
      }
      phases[key] = phase;
    }
    result.phases = phases;
  }

  return result as DocumentData;
}

/**
 * Convert Firestore Timestamp to Date safely
 */
function timestampToDate(value: unknown): Date | undefined {
  if (value && typeof (value as Timestamp).toDate === 'function') {
    return (value as Timestamp).toDate();
  }
  if (value instanceof Date) {
    return value;
  }
  return undefined;
}

export const backendTaskConverter: FirestoreDataConverter<BackendTask> = {
  toFirestore(
    task: WithFieldValue<BackendTask> | PartialWithFieldValue<BackendTask>,
    _options?: SetOptions
  ): DocumentData {
    return convertToFirestore(task as Record<string, unknown>);
  },

  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): BackendTask {
    const data = snapshot.data(options);
    const result: Record<string, unknown> = { ...data, id: snapshot.id };

    // Convert top-level Timestamp → Date
    for (const field of ['createdAt', 'updatedAt', 'cancelledAt'] as const) {
      const converted = timestampToDate(result[field]);
      if (converted) {
        result[field] = converted;
      }
    }

    // Convert phase-level Timestamp → Date
    if (result.phases && typeof result.phases === 'object') {
      const phases = result.phases as Record<string, Record<string, unknown>>;
      for (const key of Object.keys(phases)) {
        const phase = phases[key];
        for (const field of ['startedAt', 'completedAt'] as const) {
          const converted = timestampToDate(phase[field]);
          if (converted) {
            phase[field] = converted;
          }
        }
      }
    }

    return result as unknown as BackendTask;
  },
};

export default backendTaskConverter;
